import React, { useMemo, useState, useCallback } from 'react';
import {
  Controller,
  useWatch,
  type Control,
  type FieldArrayWithId,
  type UseFieldArrayAppend,
  type UseFieldArrayRemove,
  type UseFormSetValue,
} from 'react-hook-form';
import { RiskProcessAssociations } from './RiskProcessAssociations.tsx';
import { RiskControlAssociations } from './RiskControlAssociations.tsx';
import {
  AiRecommendControlsModal,
  type AiRecommendProcessTarget,
  type AiRecommendRiskContext,
  type AcceptedAiControl,
} from './AiRecommendControlsModal.tsx';
import type { RiskFormValues } from '../schema.ts';
import type { OptionLabel } from '../../../types/options.ts';
import { Tooltip, TooltipProvider } from '../../../admin/components/shared/Tooltip.tsx';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  buildProcessReturnState,
  currentPathWithSearch,
  soxProcessFlowchartPath,
} from '../../../routing/processNavigation.ts';
import { Workflow, X, Sparkles } from 'lucide-react';
import { truncateProcessDisplayName } from '../utils/riskDetailsUtils.ts';

export type ProcessPickerOption = OptionLabel & { cycleId: string; routeProcessId: string };

export type ProcessCardContext = {
  cycleId: string;
  routeProcessId: string;
  versionLabel?: string;
  statusLabel?: string;
};

interface RiskAssociationsEditorProps {
  control: Control<RiskFormValues>;
  fields: FieldArrayWithId<RiskFormValues, 'processAssociations', 'id'>[];
  append: UseFieldArrayAppend<RiskFormValues, 'processAssociations'>;
  remove: UseFieldArrayRemove;
  processOptions: ProcessPickerOption[];
  processLabelCache: Record<string, string>;
  loadingProcesses: boolean;
  loadingMoreOptions: boolean;
  processSearchInput: string;
  hasNextPage: boolean;
  onProcessSearchChange: (term: string) => void;
  onLoadMoreProcesses: () => void;
  controlOptions: OptionLabel[];
  controlLabelCache: Record<string, string>;
  loadingControls?: boolean;
  loadingMoreControls?: boolean;
  controlSearchInput?: string;
  hasMoreControls?: boolean;
  onControlSearchChange?: (term: string) => void;
  onLoadMoreControls?: () => void;
  /** UUID-keyed hints from the loaded risk (e.g. version / status). */
  processContextByUuid?: Record<string, ProcessCardContext>;
  readOnly?: boolean;
  /** When true, title bar uses negative horizontal margin to align with Risk Overview–style card padding (`p-6`). */
  bleedCardTitle?: boolean;
  /** Risk fields used as AI recommend context. */
  aiRecommendRiskContext?: AiRecommendRiskContext;
  setValue?: UseFormSetValue<RiskFormValues>;
}

export const RiskAssociationsEditor: React.FC<RiskAssociationsEditorProps> = ({
  control,
  fields,
  append,
  remove,
  processOptions,
  processLabelCache,
  loadingProcesses,
  loadingMoreOptions,
  processSearchInput,
  hasNextPage,
  onProcessSearchChange,
  onLoadMoreProcesses,
  controlOptions,
  controlLabelCache,
  loadingControls = false,
  loadingMoreControls = false,
  controlSearchInput = '',
  hasMoreControls = false,
  onControlSearchChange,
  onLoadMoreControls,
  processContextByUuid = {},
  readOnly = false,
  bleedCardTitle = false,
  aiRecommendRiskContext,
  setValue,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const selectedProcessIds = useMemo(() => fields.map((f) => f.processId), [fields]);
  const watchedAssociations = useWatch({ control, name: 'processAssociations' });
  const [aiProcessTarget, setAiProcessTarget] = useState<AiRecommendProcessTarget | null>(null);
  const [extraControlLabels, setExtraControlLabels] = useState<Record<string, string>>({});

  const mergedControlLabelCache = useMemo(
    () => ({ ...controlLabelCache, ...extraControlLabels }),
    [controlLabelCache, extraControlLabels]
  );

  const mergeContextByUuid = useMemo(() => {
    const fromOptions: Record<string, ProcessCardContext> = {};
    processOptions.forEach((opt) => {
      if (opt.value && opt.cycleId && opt.routeProcessId) {
        fromOptions[opt.value] = {
          cycleId: opt.cycleId,
          routeProcessId: opt.routeProcessId,
        };
      }
    });
    return { ...fromOptions, ...processContextByUuid };
  }, [processContextByUuid, processOptions]);

  const titleBarClassName = bleedCardTitle
    ? '-mx-6 -mt-6 mb-5 border-b border-gray-200 bg-gray-50 px-6 py-4'
    : 'mb-4 border-b border-gray-200 bg-gray-50 px-3 py-3 sm:px-4';

  const handleAiAccepted = useCallback(
    (processUuid: string, accepted: AcceptedAiControl) => {
      setExtraControlLabels((prev) => ({ ...prev, [accepted.id]: accepted.label }));
      if (!setValue) return;
      const associations = Array.isArray(watchedAssociations) ? watchedAssociations : fields;
      const idx = associations.findIndex((row) => row.processId === processUuid);
      if (idx < 0) return;
      const current = associations[idx]?.mitigatingControlIds ?? [];
      if (current.includes(accepted.id)) return;
      setValue(`processAssociations.${idx}.mitigatingControlIds`, [...current, accepted.id], {
        shouldDirty: true,
        shouldTouch: true,
      });
    },
    [fields, setValue, watchedAssociations]
  );

  return (
    <TooltipProvider>
      <div>
        <div className={titleBarClassName}>
          <h3 className="text-base font-semibold text-gray-900">Process &amp; Control Associations</h3>
        </div>
        <div className="space-y-3">
          <RiskProcessAssociations
            pickerOnly
            readOnly={readOnly}
            selectedValues={selectedProcessIds}
            processOptions={processOptions}
            selectedLabelsByValue={processLabelCache}
            loadingOptions={loadingProcesses}
            loadingMoreOptions={loadingMoreOptions}
            searchInputValue={processSearchInput}
            hasMoreOptions={hasNextPage}
            onSearchChange={onProcessSearchChange}
            onLoadMore={onLoadMoreProcesses}
            onAdd={(value) => {
              if (readOnly) return;
              if (selectedProcessIds.includes(value)) return;
              append({ processId: value, linkedNodeIds: [], mitigatingControlIds: [] });
            }}
            onRemove={(value) => {
              if (readOnly) return;
              const idx = fields.findIndex((f) => f.processId === value);
              if (idx >= 0) remove(idx);
            }}
          />

          {fields.map((fieldRow, index) => {
            const ctx = mergeContextByUuid[fieldRow.processId];
            const title =
              processLabelCache[fieldRow.processId] ??
              processOptions.find((option) => option.value === fieldRow.processId)?.label ??
              fieldRow.processId;
            const titleDisplay = truncateProcessDisplayName(title, 40);
            const versionParts: string[] = [];
            if (ctx?.versionLabel) versionParts.push(ctx.versionLabel);
            if (ctx?.statusLabel) versionParts.push(`(${ctx.statusLabel})`);
            const versionLine = versionParts.length > 0 ? versionParts.join(' ') : null;
            const canOpenFlowchart = Boolean(ctx?.cycleId && ctx?.routeProcessId);
            const flowchartPath =
              canOpenFlowchart && ctx
                ? soxProcessFlowchartPath(ctx.cycleId, ctx.routeProcessId)
                : null;

            return (
              <div
                key={fieldRow.id}
                className="rounded-lg border border-slate-200 bg-white px-3 py-3 shadow-sm sm:px-4"
              >
                <div className="flex items-stretch gap-0">
                  <div className="min-w-0 flex-1 pr-3 sm:pr-4">
                    <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                      <div className="flex min-w-0 flex-1 items-center gap-1.5">
                        <Tooltip content={title}>
                          <p className="min-w-0 max-w-full text-xs font-semibold text-slate-800">
                            {titleDisplay}
                          </p>
                        </Tooltip>
                        {flowchartPath ? (
                          <Tooltip content="Open flowchart" side="top">
                            <button
                              type="button"
                              onClick={() =>
                                navigate(flowchartPath, {
                                  state: buildProcessReturnState(currentPathWithSearch(location)),
                                })
                              }
                              className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-blue-200 bg-blue-50 text-blue-700 transition-colors hover:bg-blue-100 hover:text-blue-800"
                              aria-label={`Open flowchart for ${title}`}
                            >
                              <Workflow className="h-3.5 w-3.5" aria-hidden />
                            </button>
                          </Tooltip>
                        ) : null}
                      </div>
                      {versionLine ? (
                        <p className="text-xs font-normal text-slate-500 whitespace-nowrap">
                          {versionLine}
                        </p>
                      ) : null}
                    </div>
                  </div>

                  <div className="flex shrink-0 items-stretch">
                    {!readOnly ? (
                      <div className="flex items-center pr-2.5 sm:pr-3">
                        <button
                          type="button"
                          onClick={() => remove(index)}
                          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-rose-200 bg-rose-50 text-rose-500 shadow-sm hover:bg-rose-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rose-300"
                          title="Remove process association"
                          aria-label="Remove process association"
                        >
                          <X className="h-3 w-3 stroke-[2.75]" aria-hidden />
                        </button>
                      </div>
                    ) : null}
                    <div
                      aria-hidden
                      className="my-[18px] w-0 shrink-0 self-stretch border-l border-dotted border-[#E0E0E0]"
                    />
                    <div className="w-3 shrink-0 sm:w-4" aria-hidden />
                  </div>

                  <div className="min-w-0 flex-1 pl-0 sm:pl-0">
                    <Controller
                      control={control}
                      name={`processAssociations.${index}.mitigatingControlIds`}
                      render={({ field }) => {
                        const selectedValues = field.value ?? [];
                        return (
                          <RiskControlAssociations
                            tagLayout="pills"
                            readOnly={readOnly}
                            selectedValues={selectedValues}
                            controlOptions={controlOptions}
                            selectedLabelsByValue={mergedControlLabelCache}
                            loadingOptions={loadingControls}
                            loadingMoreOptions={loadingMoreControls}
                            searchInputValue={controlSearchInput}
                            hasMoreOptions={hasMoreControls}
                            onSearchChange={onControlSearchChange}
                            onLoadMore={onLoadMoreControls}
                            sectionTitle="Mapped Controls"
                            sectionTitleClassName="text-xs font-semibold text-slate-600"
                            sectionHint=""
                            addOptionLabel="Select control..."
                            titleActions={
                              !readOnly ? (
                                <button
                                  type="button"
                                  onClick={() =>
                                    setAiProcessTarget({
                                      processUuid: fieldRow.processId,
                                      routeProcessId:
                                        ctx?.routeProcessId ||
                                        processOptions.find(
                                          (option) => option.value === fieldRow.processId
                                        )?.routeProcessId ||
                                        '',
                                      processLabel: title,
                                    })
                                  }
                                  disabled={!aiRecommendRiskContext?.riskDescription?.trim()}
                                  className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-700 transition-colors hover:bg-blue-100 hover:text-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
                                  aria-label={`AI recommend control for ${title}`}
                                  title={
                                    aiRecommendRiskContext?.riskDescription?.trim()
                                      ? 'AI recommend control'
                                      : 'Add a risk description before recommending controls'
                                  }
                                >
                                  <Sparkles className="h-3 w-3" aria-hidden />
                                  AI recommend
                                </button>
                              ) : null
                            }
                            onAdd={(value) => field.onChange([...selectedValues, value])}
                            onRemove={(value) =>
                              field.onChange(selectedValues.filter((v) => v !== value))
                            }
                          />
                        );
                      }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {aiRecommendRiskContext ? (
          <AiRecommendControlsModal
            isOpen={Boolean(aiProcessTarget)}
            onClose={() => setAiProcessTarget(null)}
            riskContext={aiRecommendRiskContext}
            processTarget={aiProcessTarget}
            onAccepted={handleAiAccepted}
          />
        ) : null}
      </div>
    </TooltipProvider>
  );
};
